import {
  AutocompleteHighlightChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/material/useAutocomplete';
import * as React from 'react';

interface UseSelectHighlightedOptionOnTabParams<Option> {
  /** Commits the option, the same as when the user presses Enter on it. */
  selectOption: (event: React.SyntheticEvent, option: Option) => void;
  onClose?: (event: React.SyntheticEvent) => void;
}

/**
 * Makes Tab commit the highlighted option of a single-select Autocomplete
 * before focus moves to the next field. The handlers that this hook returns
 * must go to the Autocomplete. Call `trackInputChange` from its
 * `onInputChange` handler.
 *
 * Tab commits the option only after the user types a search term or moves the
 * highlight with the keyboard or mouse in the open list. The typeaheads
 * highlight the first option when they open on focus. Without this check, a
 * user who tabs through a form would fill every empty typeahead.
 */
export function useSelectHighlightedOptionOnTab<Option>({
  selectOption,
  onClose,
}: UseSelectHighlightedOptionOnTabParams<Option>) {
  const highlightedOptionRef = React.useRef<Option | null>(null);
  const userMovedHighlightRef = React.useRef(false);

  return {
    onHighlightChange: (
      _event: React.SyntheticEvent | null,
      option: Option | null,
      reason: AutocompleteHighlightChangeReason
    ) => {
      highlightedOptionRef.current = option;
      if (reason === 'keyboard' || reason === 'mouse') {
        userMovedHighlightRef.current = true;
      }
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      const option = highlightedOptionRef.current;
      if (event.key !== 'Tab' || !userMovedHighlightRef.current || !option) {
        return;
      }

      // Do not prevent the default action, so that focus still moves.
      selectOption(event, option);
    },
    onClose: (event: React.SyntheticEvent) => {
      userMovedHighlightRef.current = false;
      onClose?.(event);
    },
    trackInputChange: (
      value: string,
      reason: AutocompleteInputChangeReason
    ) => {
      // An empty search term means the user cleared the field, not that they
      // chose the first option.
      if (reason === 'input') {
        userMovedHighlightRef.current = value !== '';
      }
    },
  };
}
